import { useState, useMemo } from "react";
import { format, parseISO } from "date-fns";
import { cn } from "../lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Calendar, Clock, FileText } from "lucide-react";

// Event colors - matching EventsRow.jsx
const EVENT_COLORS = {
  blue: { name: "Blue", bg: "bg-blue-100 dark:bg-blue-900/40", border: "border-blue-300 dark:border-blue-700", text: "text-blue-800 dark:text-blue-200", solid: "bg-blue-500" },
  green: { name: "Green", bg: "bg-green-100 dark:bg-green-900/40", border: "border-green-300 dark:border-green-700", text: "text-green-800 dark:text-green-200", solid: "bg-green-500" },
  purple: { name: "Purple", bg: "bg-purple-100 dark:bg-purple-900/40", border: "border-purple-300 dark:border-purple-700", text: "text-purple-800 dark:text-purple-200", solid: "bg-purple-500" },
  red: { name: "Red", bg: "bg-red-100 dark:bg-red-900/40", border: "border-red-300 dark:border-red-700", text: "text-red-800 dark:text-red-200", solid: "bg-red-500" },
  orange: { name: "Orange", bg: "bg-orange-100 dark:bg-orange-900/40", border: "border-orange-300 dark:border-orange-700", text: "text-orange-800 dark:text-orange-200", solid: "bg-orange-500" },
  yellow: { name: "Yellow", bg: "bg-yellow-100 dark:bg-yellow-900/40", border: "border-yellow-300 dark:border-yellow-700", text: "text-yellow-800 dark:text-yellow-200", solid: "bg-yellow-500" },
  teal: { name: "Teal", bg: "bg-teal-100 dark:bg-teal-900/40", border: "border-teal-300 dark:border-teal-700", text: "text-teal-800 dark:text-teal-200", solid: "bg-teal-500" },
  pink: { name: "Pink", bg: "bg-pink-100 dark:bg-pink-900/40", border: "border-pink-300 dark:border-pink-700", text: "text-pink-800 dark:text-pink-200", solid: "bg-pink-500" },
};

const getEventColor = (colorId) => EVENT_COLORS[colorId] || EVENT_COLORS.blue;

// Event popover content
const EventPopoverContent = ({ event }) => {
  const color = getEventColor(event.color);
  
  return (
    <div className="space-y-3 p-1">
      <div className="flex items-center gap-2">
        <div className={cn("w-3 h-3 rounded-full", color.solid)} />
        <h4 className="font-semibold text-base">{event.name}</h4>
      </div>
      
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Calendar className="w-4 h-4" />
          <span>{format(parseISO(event.date), "EEEE, MMMM d, yyyy")}</span>
        </div>
        
        <div className="flex items-center gap-2 text-muted-foreground">
          <Clock className="w-4 h-4" />
          <span>{event.start_time} - {event.end_time}</span>
        </div>
        
        {event.notes && (
          <div className="flex items-start gap-2 text-muted-foreground">
            <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span className="text-sm">{event.notes}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Single event block for week view - full width within the day column
const WeekEventBlock = ({ event, rowHeight }) => {
  const color = getEventColor(event.color);
  
  return (
    <Popover>
      <PopoverTrigger asChild>
        <div
          className={cn(
            "w-full rounded cursor-pointer transition-all hover:brightness-95",
            "flex items-center px-2 overflow-hidden",
            color.bg,
            color.border,
            color.text,
            "border"
          )}
          style={{ height: `${rowHeight - 4}px` }}
          title={`${event.name} (${event.start_time} - ${event.end_time})`}
          data-testid={`week-event-${event.id}`}
        >
          <span className="text-xs font-medium truncate">{event.name}</span>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="center">
        <EventPopoverContent event={event} />
      </PopoverContent>
    </Popover>
  );
};

// Week view events row component
const WeekViewEventsRow = ({ events, weekDates, rowHeight = 28 }) => {
  // Group events by date
  const eventsByDate = useMemo(() => {
    const map = {};
    weekDates.forEach(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      map[dateStr] = events.filter(e => e.date === dateStr);
    });
    return map;
  }, [events, weekDates]);
  
  // Calculate max events on any single day to determine row count
  const maxEventsPerDay = useMemo(() => {
    let max = 0;
    weekDates.forEach(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const count = eventsByDate[dateStr]?.length || 0;
      if (count > max) max = count;
    });
    return max;
  }, [eventsByDate, weekDates]);
  
  // Check if there are any events this week
  if (maxEventsPerDay === 0) {
    return null;
  }

  const verticalPadding = 8; // 8px top + 8px bottom
  const eventRowHeight = rowHeight;
  const gap = 6; // gap between event rows (increased from 4px)
  const contentHeight = (maxEventsPerDay * eventRowHeight) + ((maxEventsPerDay - 1) * gap);
  const totalHeight = contentHeight + (verticalPadding * 2);
  
  return (
    <div 
      className="flex border-b-2 border-border/60 bg-muted/20" 
      style={{ height: `${totalHeight}px` }} 
      data-testid="week-events-row"
    >
      {/* Label column */}
      <div className="w-56 flex-shrink-0 px-2 border-r border-border bg-card sticky left-0 z-10 flex items-center">
        <span className="text-sm font-medium text-muted-foreground">Events</span>
      </div>
      
      {/* Day cells */}
      {weekDates.map((date, index) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const dayEvents = eventsByDate[dateStr] || [];
        
        return (
          <div
            key={index}
            className="flex-1 px-1 border-r border-border min-w-[100px] flex flex-col justify-start"
            style={{ paddingTop: `${verticalPadding}px`, paddingBottom: `${verticalPadding}px`, gap: `${gap}px` }}
          >
            {dayEvents.map((event) => (
              <WeekEventBlock 
                key={event.id} 
                event={event} 
                rowHeight={eventRowHeight}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default WeekViewEventsRow;
